<template>
  <div class="create-tool-container">
    <div class="content-area">
      <div class="page-header">
        <div class="header-left">
          <v-btn
            icon
            size="medium"
            variant="text"
            @click="goBack"
            class="back-btn">
            <Icon name="arrow-left" variant="solid" size="20" color="default"/>
          </v-btn>
          <h1 class="page-title">
            Create Tool
          </h1>
        </div>
      </div>

      <div class="main-content">
        <div class="content-layout">
          <!-- Stepper Sidebar -->
          <div class="stepper-sidebar">
            <!-- Step 1: Details & Integration -->
            <div class="stepper-item" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
              <div class="step-layout">
                <div class="step-indicator">
                  <div class="step-number" :class="{ 'in-progress': currentStep === 1, completed: currentStep > 1 }">
                    <Icon v-if="currentStep > 1" name="check" variant="solid" size="16" color="onPrimary"/>
                    <span v-else>1</span>
                  </div>
                  <div class="step-line" :class="{ active: currentStep >= 1 }"/>
                </div>
                <div class="step-content">
                  <h3 class="step-title" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
                    Details & Integration
                  </h3>
                </div>
              </div>
            </div>

            <!-- Step 2: Headers & Parameters -->
            <div class="stepper-item" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
              <div class="step-layout">
                <div class="step-indicator">
                  <div class="step-number" :class="{ 'in-progress': currentStep === 2, completed: currentStep > 2, next: currentStep < 2 }">
                    <Icon v-if="currentStep > 2" name="check" variant="solid" size="16" color="onPrimary"/>
                    <span v-else>2</span>
                  </div>
                  <div class="step-line" :class="{ active: currentStep > 2 }"/>
                </div>
                <div class="step-content">
                  <h3 class="step-title" :class="{ active: currentStep === 2, completed: currentStep > 2, next: currentStep < 2 }">
                    Headers & Parameters
                  </h3>
                </div>
              </div>
            </div>

            <!-- Step 3: Configurations -->
            <div class="stepper-item" :class="{ active: currentStep === 3 }">
              <div class="step-layout">
                <div class="step-indicator">
                  <div class="step-number" :class="{ 'in-progress': currentStep === 3, next: currentStep < 3 }">
                    <span>3</span>
                  </div>
                </div>
                <div class="step-content">
                  <h3 class="step-title" :class="{ active: currentStep === 3, next: currentStep < 3 }">
                    Configurations
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <!-- Form Content Area -->
          <div class="form-area">
            <!-- Step 1: Details & Integration -->
            <div v-if="currentStep === 1" class="step-content-area">
              <!-- Details Section -->
              <div class="form-section">
                <div class="section-header">
                  <h2 class="section-title">
                    Details
                  </h2>
                </div>

                <div class="section-content">
                  <!-- Info Message -->
                  <div class="info-line">
                    <div class="info-icon">
                      <Icon name="info-circle" variant="solid" size="16" color="info"/>
                    </div>
                    <p class="info-text">
                      Please note: The model takes into account the tool's name and description when deciding whether to make a tool call.
                    </p>
                  </div>

                  <!-- Tool Name Input -->
                  <div class="form-group">
                    <label class="input-label required">
                      Tool Name
                      <span class="required-asterisk">*</span>
                    </label>
                    <MankTextField
                      v-model="toolForm.name"
                      placeholder="Enter Tool Name"/>
                  </div>

                  <!-- Description Textarea -->
                  <div class="form-group">
                    <label class="input-label">Description</label>
                    <MankTextarea
                      v-model="toolForm.description"
                      placeholder="This tool will be triggered when the user asks for the time."
                      variant="outlined"
                      rows="4"
                      class="form-textarea">
                      <template v-slot:append-inner>
                        <Icon name="text-box" variant="solid" size="16" color="muted"/>
                      </template>
                    </MankTextarea>
                    <div class="helper-text">
                      {{ toolForm.description.length }}/300
                    </div>
                  </div>
                </div>
              </div>

              <!-- Integrations Section -->
              <div class="form-section">
                <div class="section-header">
                  <h2 class="section-title">
                    Integrations
                  </h2>
                </div>

                <div class="section-content">
                  <!-- Custom Endpoint URL -->
                  <div class="form-group">
                    <label class="input-label required">
                      Custom Endpoint URL
                      <span class="required-asterisk">*</span>
                    </label>
                    <MankTextField
                      v-model="toolForm.endpoint_url"
                      placeholder="https://www.mank.com/example"/>
                  </div>

                  <!-- Type Dropdown -->
                  <div class="form-group">
                    <label class="input-label required">
                      Type
                      <span class="required-asterisk">*</span>
                    </label>
                    <MankSelect
                      v-model="toolForm.method"
                      :items="httpMethods"
                      placeholder="GET"
                      :menu-props="{
                        maxHeight: 300,
                        offsetY: true,
                        nudgeBottom: 8,
                      }"
                      item-text="title"
                      item-value="value">
                      <template v-slot:append>
                        <Icon name="expand-down" variant="solid" color="muted"/>
                      </template>
                    </MankSelect>
                  </div>

                  <!-- Timeout Time -->
                  <div class="form-group">
                    <label class="input-label required">
                      Timeout Time
                      <span class="required-asterisk">*</span>
                    </label>
                    <MankTextField
                      v-model="toolForm.timeout"
                      placeholder="2,5"
                      suffix="seconds"/>
                  </div>

                  <!-- Precomputable Toggle -->
                  <div class="form-group toggle-group">
                    <label class="input-label">Precomputable</label>
                    <div class="toggle-container">
                      <div
                        class="custom-toggle"
                        :class="{ active: toolForm.pre_computable }"
                        @click="toolForm.pre_computable = !toolForm.pre_computable">
                        <div class="toggle-thumb"/>
                      </div>
                      <span class="toggle-text">{{ toolForm.pre_computable ? 'Yes' : 'No' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Headers & Parameters -->
            <div v-if="currentStep === 2" class="step-content-area">
              <!-- Headers Section -->
              <div class="form-section">
                <div class="section-header">
                  <h2 class="section-title">
                    Headers
                  </h2>
                </div>

                <div class="section-content">
                  <!-- Info Text -->
                  <div class="info-text">
                    Enter any Key/Value pairs here
                  </div>

                  <!-- Headers List -->
                  <div v-if="headersArray.length > 0" class="headers-list">
                    <div
                      v-for="(header, index) in headersArray"
                      :key="index"
                      class="header-card">
                      <!-- Inputs Area -->
                      <div class="card-inputs">
                        <!-- Key Input -->
                        <div class="form-group">
                          <label class="input-label required">
                            Key
                            <span class="required-asterisk">*</span>
                          </label>
                          <MankTextField
                            v-model="header.key"
                            placeholder="Content-Type"/>
                        </div>

                        <!-- Value Input -->
                        <div class="form-group">
                          <label class="input-label required">
                            Value
                            <span class="required-asterisk">*</span>
                          </label>
                          <MankTextField
                            v-model="header.value"
                            placeholder="application/json"/>
                        </div>
                      </div>

                      <!-- Buttons Area -->
                      <div class="card-buttons">
                        <v-btn
                          size="medium"
                          color="light-foreground-1"
                          class="mank-btn-icon"
                          elevation="0"
                          block
                          text
                          @click="removeHeader(index)">
                          <Icon name="trash" variant="solid" size="20" color="default"/>
                          Delete
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <!-- Add Header Button -->
                  <v-btn
                    variant="text"
                    @click="addHeader"
                    class="mank-btn add-item-btn">
                    <Icon name="plus" variant="solid" size="20" color="default"/>
                    Add Header
                  </v-btn>
                </div>
              </div>

              <!-- Parameters Section -->
              <div class="form-section">
                <div class="section-header">
                  <h2 class="section-title">
                    Parameters
                  </h2>
                </div>

                <div class="section-content">
                  <!-- Parameters List -->
                  <div v-if="parametersArray.length > 0" class="parameters-list">
                    <div
                      v-for="(parameter, index) in parametersArray"
                      :key="index"
                      class="parameter-card">
                      <!-- Inputs Area -->
                      <div class="card-inputs">
                        <!-- Content Toggle (Dynamic/Static/Automatic) -->
                        <div class="form-group">
                          <div class="content-toggle">
                            <div
                              v-for="option in toggleOptions"
                              :key="option.value"
                              class="toggle-item"
                              :class="{ active: parameter.contentType === option.value }"
                              @click="parameter.contentType = option.value">
                              {{ option.label }}
                            </div>
                          </div>
                        </div>

                        <!-- Name Input -->
                        <div class="form-group">
                          <label class="input-label required">
                            Name
                            <span class="required-asterisk">*</span>
                          </label>
                          <MankTextField
                            v-model="parameter.name"
                            placeholder="Enter Name"/>
                        </div>

                        <!-- Description Input -->
                        <div class="form-group">
                          <label class="input-label">Description</label>
                          <MankTextField
                            v-model="parameter.description"
                            placeholder="Enter Description"/>
                        </div>

                        <!-- Type Select -->
                        <div class="form-group">
                          <label class="input-label required">
                            Type
                            <span class="required-asterisk">*</span>
                          </label>
                          <MankSelect
                            v-model="parameter.type"
                            :items="typeOptions"
                            placeholder="String"
                            :menu-props="{
                              maxHeight: 300,
                              offsetY: true,
                              nudgeBottom: 8,
                            }"
                            item-title="title"
                            item-value="value">
                            <template v-slot:append>
                              <Icon name="expand-down" variant="solid" color="muted"/>
                            </template>
                          </MankSelect>
                        </div>

                        <!-- Location Select -->
                        <div class="form-group">
                          <label class="input-label required">
                            Location
                            <span class="required-asterisk">*</span>
                          </label>
                          <MankSelect
                            v-model="parameter.location"
                            :items="locationOptions"
                            placeholder="Query String"
                            :menu-props="{
                              maxHeight: 300,
                              offsetY: true,
                              nudgeBottom: 8,
                            }"
                            item-title="title"
                            item-value="value">
                            <template v-slot:append>
                              <Icon name="expand-down" variant="solid" color="muted"/>
                            </template>
                          </MankSelect>
                        </div>

                        <!-- Required Toggle -->

                        <div class="form-group toggle-group">
                          <label class="input-label">Required</label>
                          <div class="toggle-container">
                            <div
                              class="custom-toggle"
                              :class="{ active: parameter.required }"
                              @click="parameter.required = !parameter.required">
                              <div class="toggle-thumb"/>
                            </div>
                            <span class="toggle-text">{{ parameter.required ? 'Yes' : 'No' }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Buttons Area -->
                      <div class="card-buttons">
                        <v-btn
                          variant="text"
                          size="medium"
                          color="light-foreground-1"
                          class="mank-btn-icon"
                          elevation="0"
                          @click="removeParameter(index)">
                          <Icon name="trash" variant="solid" size="20" color="default"/>
                          Delete
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <!-- Add Parameter Button -->
                  <v-btn
                    variant="text"
                    @click="addParameter"
                    class="mank-btn add-item-btn">
                    <Icon name="plus" variant="solid" size="20" color="default"/>
                    Add Parameter
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Step 3: Configurations -->
            <div v-if="currentStep === 3" class="step-content-area">
              <div class="form-section">
                <div class="section-header">
                  <h2 class="section-title">
                    Configurations
                  </h2>
                </div>
                <div class="section-content">
                  <!-- Action Selection -->
                  <div class="form-group">
                    <label class="input-label">
                      Action
                    </label>
                    <MankSelect
                      v-model="toolForm.action"
                      :items="actionOptions"
                      placeholder="Default"
                      :menu-props="{
                        maxHeight: 300,
                        offsetY: true,
                        nudgeBottom: 8,
                      }"
                      item-title="title"
                      item-value="value">
                      <template v-slot:append>
                        <Icon name="expand-down" variant="solid" color="muted"/>
                      </template>
                    </MankSelect>
                  </div>

                  <!-- Authentication Schema -->
                  <div class="form-group">
                    <label class="input-label">
                      Authentication Schema
                    </label>
                    <MankTextField
                      v-model="toolForm.auth_schema"
                      placeholder="Bearer, Basic, etc."/>
                  </div>

                  <!-- Authentication Token -->
                  <div class="form-group">
                    <label class="input-label">
                      Authentication Token
                    </label>
                    <MankTextField
                      v-model="toolForm.auth_token"
                      placeholder="Your authentication token"/>
                  </div>

                  <!-- Static Response Toggle -->
                  <div class="form-group toggle-group">
                    <label class="input-label">Static Response</label>
                    <div class="toggle-container">
                      <div
                        class="custom-toggle"
                        :class="{ active: toolForm.static_response }"
                        @click="toolForm.static_response = !toolForm.static_response">
                        <div class="toggle-thumb"/>
                      </div>
                      <span class="toggle-text">{{ toolForm.static_response ? 'Yes' : 'No' }}</span>
                    </div>
                  </div>

                  <!-- Static Response Message -->
                  <div v-if="toolForm.static_response" class="form-group">
                    <label class="input-label">
                      Static Response Message
                    </label>
                    <MankTextarea
                      v-model="toolForm.static_response_message"
                      placeholder="Enter static response message"
                      rows="3"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="footer-buttons">
        <v-btn
          variant="text"
          size="medium"
          @click="cancel"
          class="mank-btn"
          elevation="0">
          <Icon name="cross" variant="solid" size="20" color="default"/>
          Cancel
        </v-btn>

        <v-btn
          variant="flat"
          color="primary"
          size="medium"
          @click="nextStep"
          :disabled="!canProceed"
          class="mank-btn"
          elevation="0">
          {{ currentStep === 3 ? 'Create Tool' : 'Next' }}
          <Icon v-if="currentStep < 3"
                name="arrow-right"
                variant="solid"
                size="20"
                color="onPrimary"
                class="ml-4 "/>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import ToolService from '../services/ToolService';

export default {
  name: 'CreateTool',
  components: { Icon },
  data() {
    return {
      currentStep: 1,
      toolForm: {
        name: '',
        description: '',
        endpoint_url: '',
        method: 'GET',
        timeout: 2.5,
        pre_computable: false,
        static_response: false,
        static_response_message: '',
        action: 'Default',
        auth_schema: '',
        auth_token: '',
        headers: {},
        parameters: {},
      },
      headersArray: [
        { key: 'Content-Type', value: 'application/json' },
        { key: 'Authorization', value: 'Bearer token' },
      ],
      parametersArray: [
        {
          contentType: 'dynamic',
          name: 'api_key',
          description: 'API key for authentication',
          type: 'string',
          location: 'query',
          required: true,
        },
      ],
      httpMethods: [
        { title: 'GET', value: 'GET' },
        { title: 'POST', value: 'POST' },
        { title: 'PUT', value: 'PUT' },
        { title: 'DELETE', value: 'DELETE' },
        { title: 'PATCH', value: 'PATCH' },
      ],
      actionOptions: [
        { title: 'Default', value: 'Default' },
        { title: 'Speaks', value: 'Speaks' },
        { title: 'Listens', value: 'Listens' },
        { title: 'Speaks Once', value: 'SpeaksOnce' },
      ],
      // Parameter options
      toggleOptions: [
        { label: 'Dynamic', value: 'dynamic' },
        { label: 'Static', value: 'static' },
        { label: 'Automatic', value: 'automatic' },
      ],
      typeOptions: [
        { title: 'String', value: 'string' },
        { title: 'Number', value: 'number' },
        { title: 'Boolean', value: 'boolean' },
        { title: 'Array', value: 'array' },
        { title: 'Object', value: 'object' },
      ],
      locationOptions: [
        { title: 'Query String', value: 'query' },
        { title: 'Path', value: 'path' },
        { title: 'Header', value: 'header' },
        { title: 'Body', value: 'body' },
      ],
    };
  },
  computed: {
    canProceed() {
      if (this.currentStep === 1) {
        return this.toolForm.name.trim() !== '';
      }
      return true;
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    cancel() {
      this.$router.push({ name: 'Tools' });
    },
    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep += 1;
      } else {
        this.createTool();
      }
    },
    async createTool() {
      try {
        // Validate required fields
        if (!this.toolForm.name || !this.toolForm.endpoint_url || !this.toolForm.method) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Validation Error',
            message: 'Please fill in all required fields (Name, Endpoint URL, Method)',
          });
          return;
        }

        // Convert arrays to objects for API
        const headersObject = {};
        this.headersArray.forEach((header) => {
          if (header.key && header.value) {
            headersObject[header.key] = header.value;
          }
        });

        const parametersObject = {};
        this.parametersArray.forEach((param) => {
          if (param.name) {
            parametersObject[param.name] = {
              type: param.type,
              description: param.description,
              required: param.required,
              location: param.location,
              contentType: param.contentType,
            };
          }
        });

        const toolData = {
          ...this.toolForm,
          headers: headersObject,
          parameters: parametersObject,
          timeout: parseFloat(this.toolForm.timeout),
        };

        await ToolService.createTool(toolData);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Tool created successfully',
        });

        this.$router.push({ name: 'Tools' });
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to create tool',
        });
      }
    },
    // Header Methods
    addHeader() {
      this.headersArray.push({
        key: '',
        value: '',
      });
    },
    removeHeader(index) {
      this.headersArray.splice(index, 1);
    },

    // Parameter Methods
    addParameter() {
      this.parametersArray.push({
        contentType: 'dynamic',
        name: '',
        description: '',
        type: 'string',
        location: 'query',
        required: false,
      });
    },
    removeParameter(index) {
      this.parametersArray.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.main-content {
  overflow: hidden;

  .content-layout {
    flex: 1;
    display: flex;
    flex-direction: row;
    overflow: hidden;
  }
}

.stepper-sidebar {
  width: 280px;
  padding: 0 24px;
  border-right: 1px solid $light-border;
  background-color: $light-surface-2;
    display: flex;
    flex-direction: column;

    .stepper-item {
      height: 120px;
    display: flex;
    align-items: stretch;

      .step-layout {
        display: flex;
        align-items: center;
        gap: 8px;
      width: 100%;

        .step-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 32px;
        position: relative;

          .step-number {
            width: 32px;
            height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: $body-font-family;
            font-size: $body-2-size;
          font-weight: 600;
          border: 1px solid $light-border;
          background-color: $light-surface-2;
          color: $light-foreground-3;
          position: relative;
          z-index: 2;

          &.in-progress {
            background-color: $light-primary;
            color: $light-surface-2;
            border-color: $light-primary;
          }

          &.completed {
            background-color: $light-primary;
            color: $light-surface-2;
            border-color: $light-primary;
          }

          &.next {
            background-color: $light-surface-2;
            color: $light-foreground-3;
            border-color: $light-border;
          }
          }

          .step-line {
          width: 2px;
          height: 72px;
          background-color: $light-surface-3;
          margin-top: 8px;
          position: absolute;
          top: 32px;
          left: 14px;
          z-index: 1;

          &.active {
            background-color: $light-primary;
          }
        }

        // Hide the top connecting line for the first step
        .stepper-item:first-child &::before {
          display: none;
        }

        // Active state for connecting lines
        &.connected::before {
          background-color: $light-primary;
          }
        }

        .step-content {
          flex: 1;

          .step-title {
          font-family: $body-font-family;
            font-size: $body-3-size;
          font-weight: 700;
          color: $light-foreground-3;
            margin: 0;

      &.active {
            color: $light-foreground-1;
      }

      &.completed {
            color: $light-foreground-1;
          }

          &.next {
            color: $light-foreground-3;
          }
        }
      }
    }
  }
}

.form-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

.step-content-area {
  flex: 1;
  display: flex;
    flex-direction: row;
    gap: 0;

    .form-section {
      border-bottom: 1px solid $light-border;
      width: 50%;
      &:first-child {
        border-right: 1px solid $light-border;
      }
  .section-header {
    padding: 8px 24px;
        background-color: $light-surface-1;
        border-bottom: 1px solid $light-border;

    .section-title {
          font-family: $body-font-family;
      font-size: $body-2-size;
          font-weight: 700;
          color: $light-foreground-1;
      margin: 0;
    }
  }

  .section-content {
        padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;

        .info-line {
      display: flex;
      align-items: center;
      gap: 8px;
          padding: 8px;
        background-color: #EDF3FE;
        border-radius: 4px;

          .info-icon {
            flex-shrink: 0;
      }

      .info-text {
            font-family: $body-font-family;
        font-size: $body-3-size;
            font-weight: 400;
            color: $light-foreground-3;
        line-height: 1.6;
        margin: 0;
          }
        }

        .info-text {
          font-family: $body-font-family;
          font-size: $body-3-size;
          font-weight: 400;
          color: $light-foreground-3;
          line-height: 1.6;
        }

        .headers-list,
        .parameters-list {
      display: flex;
      flex-direction: column;
          gap: 24px;
        }

        .header-card,
        .parameter-card {
          background-color: $light-surface-1;
          border-radius: 12px;
          overflow: hidden;

          .card-inputs {
            padding: 16px;
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .card-buttons {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 4px 16px;
            border-top: 1px solid $light-border;

          }
        }

        .content-toggle {
          display: flex;
          align-items: center;
          background-color: $light-surface-3;
          border-radius: 12px;
          padding: 4px;
          height: 32px;

          .toggle-item {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
          border-radius: 8px;
            font-family: $body-font-family;
          font-size: $body-3-size;
            font-weight: 600;
            color: $light-foreground-4;
            cursor: pointer;
            transition: all 0.2s ease;

            &.active {
              background-color: $light-surface-2;
              color: $light-foreground-1;
            }

            &:hover:not(.active) {
              color: $light-foreground-3;
            }
          }
        }

        .toggle-group {
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        }

        .toggle-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .custom-toggle {
          width: 64px;
          height: 24px;
          background-color: $light-surface-3;
          border-radius: 20px;
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 3px 8px 3px 3px;
          display: flex;
          align-items: center;

          &.active {
            background-color: $light-primary;
          }

          .toggle-thumb {
            width: 18px;
            height: 18px;
            background-color: $light-surface-2;
            border-radius: 50%;
            transition: all 0.3s ease;
            transform: translateX(0);
          }

          &.active .toggle-thumb {
            transform: translateX(37px);
          }
        }

        .toggle-text {
          font-family: $body-font-family;
        font-size: $caption-1-size;
          font-weight: 600;
          color: $light-foreground-3;
        }

        .add-item-btn {
          width: 100%;
          border: 1px dashed $light-border;
          background-color: $light-surface-2;
          color: $light-foreground-1;
          font-weight: 600;

          &:hover {
            border-color: $light-primary;
            background-color: rgba(235, 87, 93, 0.02);
          }
        }

        .form-group {
  display: flex;
  flex-direction: column;
          gap: 8px;

          &.toggle-group {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }

          .input-label {
            font-family: $body-font-family;
    font-size: $body-3-size;
            font-weight: 600;
            color: $light-foreground-1;
    margin: 0;

            &.required {
              .required-asterisk {
                color: $light-primary;
                margin-left: 2px;
              }
            }
          }

          .toggle-container {
            display: flex;
            align-items: center;
            gap: 8px;

            .custom-toggle {
              width: 64px;
              height: 24px;
              background-color: $light-surface-1;
              border-radius: 20px;
              position: relative;
              cursor: pointer;
              transition: all 0.2s ease;
              padding: 3px;

              &.active {
                background-color: $light-primary;
              }

              .toggle-thumb {
                width: 18px;
                height: 18px;
                background-color: $light-surface-2;
                border-radius: 50%;
                transition: all 0.2s ease;
                transform: translateX(0);
              }

              &.active .toggle-thumb {
                transform: translateX(40px);
              }
            }

            .toggle-text {
              font-family: $body-font-family;
              font-size: $caption-1-size;
              font-weight: 600;
              color: $light-foreground-3;
            }
          }

          .helper-text {
            font-family: $body-font-family;
            font-size: $caption-1-size;
            font-weight: 400;
            color: $light-foreground-4;
            text-align: right;
          }
        }
      }
    }
  }
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid $light-border;
  background-color: $light-surface-2;

  .cancel-btn,
  .next-btn {
    height: 32px;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .next-btn {
    width: 100px;
    }
    }

</style>
